import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosClient from "../../api/axiosClient";
import ContentEditor from "./components/ContentEditor";

const initialForm = {
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    featuredImage: "",
    status: "draft",
    categoryIds: [],
    tagIds: [],
};

const EditPost = () => {
    const { postId } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(initialForm);
    const [tags, setTags] = useState([]);
    const [categories, setCategories] = useState([]);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [apiError, setApiError] = useState("");
    
    useEffect(() => {
        fetchTags();
        fetchCategories();
        fetchPost();
        // eslint-disable-next-line
    }, [postId]);
    
    const fetchTags = async () => {
        try {
            const res = await axiosClient.get("/tags/");
            setTags(res.data.data || []);
        } catch {
            setTags([]);
        }
    };
    
    const fetchCategories = async () => {
        try {
            const res = await axiosClient.get("/categories/");
            setCategories(res.data.data || []);
        } catch {
            setCategories([]);
        }
    };
    
    const fetchPost = async () => {
        setApiError("");
        try {
            const res = await axiosClient.get(`/posts/${postId}`);
            const d = res.data.data;
            setForm({
                title: d.title || "",
                slug: d.slug || "",
                excerpt: d.excerpt || "",
                content: d.content || "",
                featuredImage: d.featuredImage || "",
                status: d.status || "draft",
                categoryIds: Array.isArray(d.categories)
                    ? d.categories.map((cat) => cat.categoryId)
                    : [],
                tagIds: Array.isArray(d.tags)
                    ? d.tags.map((tag) => tag.tagId)
                    : [],
            });
        } catch (err) {
            setApiError("Failed to fetch post.");
        }
    };
    
    const handleChange = (e) => {
        const { name, value, type, selectedOptions } = e.target;
        if (type === "select-multiple") {
            setForm({
                ...form,
                [name]: Array.from(selectedOptions, (option) => Number(option.value)),
            });
        } else {
            setForm({ ...form, [name]: value });
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setApiError("");
        setMessage("");
        try {
            await axiosClient.put(`/posts/${postId}`, {
                ...form,
                tagIds: form.tagIds,
                categoryIds: form.categoryIds,
            });
            setMessage("Post updated!");
            setTimeout(() => navigate("/posts"), 1200);
        } catch (err) {
            setApiError(
                err.response?.data?.message || "Failed to update post. Please try again."
            );
        } finally {
            setSaving(false);
        }
    };
    
    return (
        <div className="container mt-4" style={{ maxWidth: "850px" }}>
            <h2 className="mb-4">Edit Post</h2>
            {apiError && <div className="alert alert-danger">{apiError}</div>}
            {message && <div className="alert alert-success">{message}</div>}
            
            <form onSubmit={handleSubmit}>
                <div className="mb-3">
                    <label className="form-label">Title</label>
                    <input
                        className="form-control"
                        name="title"
                        value={form.title}
                        onChange={handleChange}
                        required
                    />
                </div>
                
                <div className="mb-3">
                    <label className="form-label">Slug</label>
                    <input
                        className="form-control"
                        name="slug"
                        value={form.slug}
                        onChange={handleChange}
                        required
                    />
                </div>
                
                <div className="mb-3">
                    <label className="form-label">Excerpt</label>
                    <textarea
                        className="form-control"
                        name="excerpt"
                        value={form.excerpt}
                        onChange={handleChange}
                        rows={2}
                    />
                </div>
                
                <div className="mb-3">
                    <label className="form-label">Featured Image URL</label>
                    <input
                        className="form-control"
                        name="featuredImage"
                        value={form.featuredImage}
                        onChange={handleChange}
                    />
                </div>
                
                <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                        className="form-select"
                        name="status"
                        value={form.status}
                        onChange={handleChange}
                    >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                    </select>
                </div>
                
                <div className="mb-3">
                    <label className="form-label">Categories</label>
                    <select
                        className="form-select"
                        name="categoryIds"
                        value={form.categoryIds}
                        multiple
                        onChange={handleChange}
                    >
                        {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                                {cat.name}
                            </option>
                        ))}
                    </select>
                </div>
                
                <div className="mb-3">
                    <label className="form-label">Tags</label>
                    <select
                        className="form-select"
                        name="tagIds"
                        value={form.tagIds}
                        multiple
                        onChange={handleChange}
                    >
                        {tags.map((tag) => (
                            <option key={tag.id} value={tag.id}>
                                {tag.name}
                            </option>
                        ))}
                    </select>
                </div>
                
                <div className="mb-3">
                    <label className="form-label">Content</label>
                    <ContentEditor
                        value={form.content}
                        onChange={(content) =>
                            setForm((prev) => ({ ...prev, content }))
                        }
                    />
                </div>
                
                <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? "Saving..." : "Update Post"}
                </button>
            </form>
        </div>
    );
};

export default EditPost;
